import * as XLSX from 'xlsx';
import type { ScoredGeography } from '@/types/insights';
import type { ReportFilters } from '@/types/report';
import { describeFilters } from '@/reports/filterSummary';
import { round } from '@/utils/numbers';

/**
 * Generates the Report Builder's Excel export: a "Report Info" sheet
 * (title, filters, timestamp) plus a "Counties" data sheet with one row per
 * matching county. Runs entirely client-side via SheetJS -- no backend.
 */
export function exportReportExcel(rows: ScoredGeography[], filters: ReportFilters, filename = 'texas-market-trends-report.xlsx') {
  const workbook = XLSX.utils.book_new();

  const infoSheet = XLSX.utils.aoa_to_sheet([
    ['Texas Market Trends Report'],
    [],
    ['Filters', describeFilters(filters)],
    ['Generated', new Date().toLocaleString()],
    ['Counties matched', rows.length],
    [],
    ['Figures are derived by MAY Assistant from public source data and are not official published figures.'],
    ['Price-per-square-foot is not yet available in this dataset.'],
  ]);
  infoSheet['!cols'] = [{ wch: 18 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(workbook, infoSheet, 'Report Info');

  const dataRows = rows.map(({ record, insights }) => ({
    County: record.geoName,
    Period: record.periodLabel,
    'Median Sale Price': record.resolved.medianSalePrice ?? null,
    'Median Days on Market': record.resolved.medianDaysOnMarket ?? null,
    'New Listings': record.resolved.newListings ?? null,
    'Closed Sales': record.resolved.closedSales ?? null,
    'Months of Inventory': record.resolved.monthsOfInventory ?? null,
    'Sale-to-List Ratio %': record.resolved.saleToListRatioPct != null ? round(record.resolved.saleToListRatioPct, 1) : null,
    'Price Reduction Share %': record.resolved.priceReductionSharePct != null ? round(record.resolved.priceReductionSharePct, 1) : null,
    'Hot/Cold Label': insights.hotColdScore.label,
    'Hot/Cold Score': round(insights.hotColdScore.value, 1),
    'Opportunity Score': round(insights.opportunityScore.value, 1),
    'Cooling Risk': round(insights.coolingRisk.value, 1),
    'Confidence Score': round(insights.confidenceScore.value, 1),
    'Contributing Sources': insights.contributingSources.join(', '),
    'Is Sample Data': insights.isSampleData,
  }));
  const dataSheet = XLSX.utils.json_to_sheet(dataRows);
  dataSheet['!cols'] = Object.keys(dataRows[0] ?? {}).map(() => ({ wch: 18 }));
  XLSX.utils.book_append_sheet(workbook, dataSheet, 'Counties');

  XLSX.writeFile(workbook, filename);
}
